<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import EntityRow from '@/components/workspace/EntityRow.vue';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import TaskMilestoneSelect from '@/components/tasks/TaskMilestoneSelect.vue';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, ExternalLink, Workflow } from 'lucide-vue-next';
import { ref } from 'vue';
import type { Approval, Project, Task } from '~/types/models';
import type { ClientTaskDraft } from './types';

interface Props {
    canMutate: boolean;
    pendingTasks: Task[];
    pendingApprovals: Approval[];
    relatedProjects: Project[];
    tasksHref: string;
    approvalsHref: string;
    relatedProjectNameById: (projectId?: string | null) => string;
    createTask: (draft: ClientTaskDraft) => Promise<boolean>;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: 'open-task', taskId: string): void,
    (e: 'open-approval', approval: Approval): void,
}>();

const isCreateTaskOpen = ref(false);
const taskCreating = ref(false);
const taskDraft = ref<ClientTaskDraft>(buildEmptyTaskDraft());

const projectOptions = computed(() => [
    { value: '', label: 'Client only' },
    ...props.relatedProjects.map(project => ({ value: project.id, label: project.name })),
]);

const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
];

function buildEmptyTaskDraft(): ClientTaskDraft {
    return {
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        projectId: '',
        milestoneId: '',
        clientVisible: false,
        clientResponseRequired: false,
    };
}

function resetTaskDraft() {
    taskDraft.value = buildEmptyTaskDraft();
}

watch(() => taskDraft.value.projectId, () => {
    taskDraft.value.milestoneId = '';
});

async function handleTaskCreated() {
    taskCreating.value = true;
    try {
        const created = await props.createTask({
            title: taskDraft.value.title.trim(),
            description: taskDraft.value.description.trim(),
            priority: taskDraft.value.priority,
            dueDate: taskDraft.value.dueDate,
            projectId: taskDraft.value.projectId,
            milestoneId: taskDraft.value.milestoneId,
            clientVisible: taskDraft.value.clientVisible,
            clientResponseRequired: taskDraft.value.clientVisible ? taskDraft.value.clientResponseRequired : false,
        });

        if (!created) return;

        resetTaskDraft();
        isCreateTaskOpen.value = false;
    } finally {
        taskCreating.value = false;
    }
}
</script>

<template>
    <div class="space-y-4">
    <SectionSurface title="Pending tasks" description="Tasks still in flight for this client." :icon="Workflow" density="compact" presentation="editorial">
        <template v-if="canMutate" #actions>
            <div class="flex flex-wrap gap-2">
            <Sheet v-model:open="isCreateTaskOpen">
                <Button variant="outline" size="sm" class="rounded-full" @click="isCreateTaskOpen = true">
                    <Workflow class="mr-2 size-4" />
                    Create task
                </Button>
                <SheetContent class="w-full overflow-y-auto sm:max-w-xl">
                    <SheetHeader class="text-left">
                        <SheetTitle>Create task</SheetTitle>
                        <SheetDescription>Add a new task directly to this client, with or without a linked project.</SheetDescription>
                    </SheetHeader>

                    <div class="space-y-4">
                        <div class="space-y-2">
                            <Label for="client-task-title">Task title</Label>
                            <Input id="client-task-title" v-model="taskDraft.title" placeholder="Send proposal follow-up" />
                        </div>

                        <div class="space-y-2">
                            <Label for="client-task-description">Description</Label>
                            <Textarea
                                id="client-task-description"
                                v-model="taskDraft.description"
                                rows="4"
                                placeholder="Capture what needs to happen next."
                            />
                        </div>

                        <div class="grid gap-4 sm:grid-cols-2">
                            <div class="space-y-2">
                                <Label for="client-task-project">Project</Label>
                                <SearchableSelectMenu
                                    id="client-task-project"
                                    v-model="taskDraft.projectId"
                                    :options="projectOptions"
                                    placeholder="Client only"
                                    search-placeholder="Search projects..."
                                    show-all-by-default
                                    variant="form"
                                />
                            </div>

                            <TaskMilestoneSelect
                                v-model="taskDraft.milestoneId"
                                :project-id="taskDraft.projectId"
                                :project-options="relatedProjects"
                                storage-key="client-task-milestone"
                            />

                            <div class="space-y-2">
                                <Label for="client-task-priority">Priority</Label>
                                <SearchableSelectMenu
                                    id="client-task-priority"
                                    v-model="taskDraft.priority"
                                    :options="priorityOptions"
                                    placeholder="Choose priority"
                                    show-all-by-default
                                    variant="form"
                                />
                            </div>
                        </div>

                        <div class="space-y-2">
                            <Label for="client-task-due">Due date</Label>
                            <Input id="client-task-due" v-model="taskDraft.dueDate" type="date" />
                        </div>

                        <label class="flex items-start gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-3 text-sm">
                            <input v-model="taskDraft.clientVisible" type="checkbox" class="mt-1 size-4 rounded border-border" />
                            <span>
                                <span class="block font-medium text-foreground">Visible in client portal</span>
                                <span class="block text-muted-foreground">Use this when the task should appear in the client-facing workspace.</span>
                            </span>
                        </label>

                        <label
                            v-if="taskDraft.clientVisible"
                            class="flex items-start gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-3 text-sm"
                        >
                            <input v-model="taskDraft.clientResponseRequired" type="checkbox" class="mt-1 size-4 rounded border-border" />
                            <span>
                                <span class="block font-medium text-foreground">Needs client response</span>
                                <span class="block text-muted-foreground">Mark this when the client should respond before the task can move forward.</span>
                            </span>
                        </label>

                        <div class="flex justify-end gap-2">
                            <Button variant="outline" class="rounded-full" @click="isCreateTaskOpen = false">Cancel</Button>
                            <Button class="rounded-full" :disabled="!taskDraft.title.trim() || taskCreating" @click="handleTaskCreated">
                                {{ taskCreating ? 'Creating...' : 'Create task' }}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            </div>
        </template>

        <EmptyState v-if="pendingTasks.length === 0" embedded title="No pending tasks" description="Open client work will appear here." />
        <div v-else>
            <EntityRow v-for="task in pendingTasks.slice(0, 5)" :key="task.id" :title="task.title" :description="`${relatedProjectNameById(task.projectId) || task.client || 'Client task'} • ${task.status || 'Open'}`" :icon="Workflow" interactive @activate="emit('open-task', task.id)">
                <template #trailing><Badge v-if="task.clientResponseRequired" variant="secondary">Client response</Badge><ExternalLink class="size-3.5" /></template>
            </EntityRow>
            <Button v-if="pendingTasks.length > 5" variant="ghost" size="sm" class="mt-3" as-child><NuxtLink :to="tasksHref">View all tasks</NuxtLink></Button>
        </div>
    </SectionSurface>

    <SectionSurface title="Pending approvals" description="Client decisions and sign-offs still awaiting a response." :icon="AlertTriangle" density="compact" presentation="editorial">
        <EmptyState v-if="pendingApprovals.length === 0" embedded title="No pending approvals" description="Approval requests awaiting a response will appear here." />
        <div v-else>
            <EntityRow v-for="approval in pendingApprovals.slice(0, 5)" :key="approval.id" :title="approval.stageName || 'Project approval'" :description="`${approval.projectName || 'Project approval'} • awaiting response`" :icon="AlertTriangle" meta="Needs review" interactive @activate="emit('open-approval', approval)" />
            <Button v-if="pendingApprovals.length > 5" variant="ghost" size="sm" class="mt-3" as-child><NuxtLink :to="approvalsHref">View all approvals</NuxtLink></Button>
        </div>
    </SectionSurface>
    </div>
</template>
